import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Building2, LayoutGrid, KeySquare } from 'lucide-react';

function normalizePerms(perms) {
  if (Array.isArray(perms)) {
    const dict = {};
    perms.forEach(p => { dict[p] = 'write'; });
    return dict;
  }
  return (typeof perms === 'object' && perms !== null) ? perms : {};
}

// iter822 — "My Access" summary: at-a-glance read-only view of exactly
// which branches + app modules + per-module permissions a user has.
export function UserAccessSummaryDialog({ user, branches, allModules, appModules, onClose }) {
  if (!user) return null;
  const isAdmin = user.role === 'admin';
  const allowedBranchIds = (user.allowed_branches || []).filter(Boolean);
  const effectiveBranchIds = allowedBranchIds.length ? allowedBranchIds : (user.branch_id ? [user.branch_id] : []);
  const branchNames = effectiveBranchIds.map(id => branches.find(b => b.id === id)?.name || id);
  const mods = user.allowed_modules || [];
  const perms = normalizePerms(user.permissions);
  const write = allModules.filter(m => perms[m.value] === 'write');
  const read = allModules.filter(m => perms[m.value] === 'read');
  const none = allModules.filter(m => !perms[m.value] || perms[m.value] === 'none');

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="user-access-summary-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <ShieldCheck size={18} className="text-orange-500" />
            Access Summary — {user.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Role:</span>
            <Badge variant="outline" className="capitalize">{user.role}</Badge>
            {isAdmin && <Badge className="bg-success/20 text-success border-success/30">Full access</Badge>}
          </div>

          <div>
            <div className="flex items-center gap-1.5 font-semibold mb-1.5">
              <Building2 size={14} className="text-muted-foreground" /> Branches they can see
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="access-summary-branches">
              {effectiveBranchIds.length === 0 ? (
                <Badge className="bg-success/20 text-success border-success/30">All branches</Badge>
              ) : branchNames.map(n => (
                <Badge key={n} variant="outline" className="border-orange-300 text-orange-600">{n}</Badge>
              ))}
            </div>
            {effectiveBranchIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Sales, expenses, dashboards and reports are limited to {effectiveBranchIds.length === 1 ? 'this branch' : 'these branches'} only.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 font-semibold mb-1.5">
              <LayoutGrid size={14} className="text-muted-foreground" /> App modules (launcher)
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="access-summary-modules">
              {mods.length === 0 || mods.length >= appModules.length ? (
                <Badge className="bg-success/20 text-success border-success/30">All modules</Badge>
              ) : mods.map(m => {
                const meta = appModules.find(a => a.id === m);
                return <Badge key={m} className={meta?.color || 'bg-muted text-muted-foreground'}>{meta?.label || m}</Badge>;
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1.5 font-semibold mb-1.5">
              <KeySquare size={14} className="text-muted-foreground" /> Module permissions
            </div>
            {isAdmin ? (
              <p className="text-xs text-muted-foreground">Admins have write access to every module.</p>
            ) : (
              <div className="space-y-2" data-testid="access-summary-permissions">
                {write.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] font-semibold uppercase text-success w-14 shrink-0">Write</span>
                    {write.map(m => <Badge key={m.value} className="bg-success/15 text-success border-success/30 text-[10px]">{m.label}</Badge>)}
                  </div>
                )}
                {read.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] font-semibold uppercase text-info w-14 shrink-0">Read</span>
                    {read.map(m => <Badge key={m.value} className="bg-info/15 text-info border-info/30 text-[10px]">{m.label}</Badge>)}
                  </div>
                )}
                {none.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] font-semibold uppercase text-muted-foreground w-14 shrink-0">None</span>
                    {none.map(m => <Badge key={m.value} variant="outline" className="text-muted-foreground text-[10px]">{m.label}</Badge>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
